import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, csrfHeader, type PlanDto } from "../api/client";
import { PlanDecision } from "../components/PlanDecision";
import { isEndCommand } from "./voiceCommands";

type Phase = "connecting" | "listening" | "thinking" | "speaking" | "ended" | "error";
type Line = { who: "du" | "planer"; text: string };

const MAX_SECONDS = 200;
const IDLE_SECONDS = 25;

/** Schnittstelle für die Android-Hülle: Aktivierungswort erkannt → Gespräch; Ende → Mikrofon zurück an die Wort-Erkennung. */
declare global {
  interface Window {
    fpVoice?: { start: (area?: string) => void };
    FamilyPlannerNative?: { conversationEnded?: () => void; conversationStarted?: () => void };
  }
}

/**
 * Sprachgespräch mit dem Planer (ADR-0014): WebRTC direkt zum Anbieter, Aufbau und Werkzeuge über unseren Server.
 * Das Fenster zeigt Zustand, Mitschrift und – sobald der Planer einen Vorschlag macht – die Karte dazu. Absagen/Löschen
 * werden hier per Antippen bestätigt; gesprochenes „trag ein“ speichert der Server selbst.
 */
export function VoiceConversation({ area, onClose }: { area?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>("connecting");
  const [lines, setLines] = useState<Line[]>([]);
  const [plan, setPlan] = useState<PlanDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pc = useRef<RTCPeerConnection | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const lastActivity = useRef(Date.now());
  const ended = useRef(false);

  const finish = () => {
    if (ended.current) return;
    ended.current = true;
    stream.current?.getTracks().forEach((t) => t.stop());
    pc.current?.close();
    setPhase("ended");
    void qc.invalidateQueries();
    window.FamilyPlannerNative?.conversationEnded?.();
    window.setTimeout(onClose, 1500);
  };

  const showPlan = async (planId: string) => {
    try {
      const next = await api<PlanDto>(`/api/v1/action-plans/${planId}`);
      qc.setQueryData(["plan", planId], next);
      setPlan(next);
    } catch {
      // Karte ist nur Zusatz – das Gespräch läuft weiter.
    }
  };

  const onEvent = (raw: string) => {
    let e: Record<string, unknown>;
    try {
      e = JSON.parse(raw);
    } catch {
      return;
    }
    lastActivity.current = Date.now();
    switch (e.type) {
      case "input_audio_buffer.speech_started":
        setPhase("listening");
        break;
      case "input_audio_buffer.speech_stopped":
        setPhase("thinking");
        break;
      case "conversation.item.input_audio_transcription.completed":
        if (typeof e.transcript === "string" && e.transcript.trim()) {
          setLines((l) => [...l.slice(-6), { who: "du", text: e.transcript as string }]);
          // „beenden“ / „Tschüss“ / „danke, das war's“: sofort schließen (der Server legt parallel auf).
          if (isEndCommand(e.transcript)) finish();
        }
        break;
      case "response.output_audio_transcript.done":
        if (typeof e.transcript === "string" && e.transcript.trim()) setLines((l) => [...l.slice(-6), { who: "planer", text: e.transcript as string }]);
        break;
      case "output_audio_buffer.started":
        setPhase("speaking");
        break;
      case "output_audio_buffer.stopped":
        setPhase("listening");
        break;
      case "conversation.item.added":
      case "conversation.item.done":
      case "response.output_item.added":
      case "response.output_item.done": {
        const item = e.item as { type?: string; output?: string; name?: string } | undefined;
        // Das Modell hat „beenden“ verstanden → sofort schließen (der Server legt parallel auf).
        if (item?.type === "function_call" && item.name === "end_conversation") {
          finish();
          break;
        }
        if (item?.type === "function_call_output" && item.output) {
          try {
            const out = JSON.parse(item.output) as { plan_id?: string; saved?: boolean };
            if (out.plan_id) void showPlan(out.plan_id);
            if (out.saved) void qc.invalidateQueries();
          } catch {
            // ignorieren
          }
        }
        break;
      }
    }
  };

  useEffect(() => {
    window.FamilyPlannerNative?.conversationStarted?.();
    (async () => {
      try {
        stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch {
        setError("Kein Zugriff auf das Mikrofon.");
        setPhase("error");
        return;
      }
      const conn = new RTCPeerConnection();
      pc.current = conn;
      conn.ontrack = (ev) => {
        if (audio.current) audio.current.srcObject = ev.streams[0];
      };
      conn.onconnectionstatechange = () => {
        if (["failed", "closed", "disconnected"].includes(conn.connectionState)) finish();
      };
      stream.current.getTracks().forEach((t) => conn.addTrack(t, stream.current!));
      const channel = conn.createDataChannel("oai-events");
      channel.onmessage = (m) => onEvent(String(m.data));
      channel.onclose = () => finish();
      channel.onopen = () => setPhase("thinking");

      const offer = await conn.createOffer();
      await conn.setLocalDescription(offer);
      const res = await fetch(`/api/v1/voice/calls${area ? `?area=${encodeURIComponent(area)}` : ""}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/sdp", Accept: "application/sdp, application/json", ...(await csrfHeader()) },
        body: offer.sdp,
      });
      if (!res.ok) {
        const problem = await res.json().catch(() => null) as { title?: string } | null;
        setError(problem?.title ?? "Der Sprachassistent ist gerade nicht verfügbar.");
        setPhase("error");
        stream.current.getTracks().forEach((t) => t.stop());
        conn.close();
        window.FamilyPlannerNative?.conversationEnded?.();
        return;
      }
      await conn.setRemoteDescription({ type: "answer", sdp: await res.text() });
    })().catch((e) => {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("error");
    });

    const started = Date.now();
    const timer = window.setInterval(() => {
      const now = Date.now();
      if (now - started > MAX_SECONDS * 1000 || now - lastActivity.current > IDLE_SECONDS * 1000) finish();
    }, 1000);
    return () => {
      window.clearInterval(timer);
      if (!ended.current) {
        ended.current = true;
        stream.current?.getTracks().forEach((t) => t.stop());
        pc.current?.close();
        window.FamilyPlannerNative?.conversationEnded?.();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const labels: Record<Phase, string> = {
    connecting: "Verbinde …",
    listening: "Ich höre zu …",
    thinking: "Einen Moment …",
    speaking: "Ich spreche …",
    ended: "Bis später!",
    error: "Nicht verfügbar",
  };

  return (
    <div className="dialog-backdrop voice-backdrop" role="dialog" aria-modal="true" aria-label="Sprachgespräch" onPointerDown={() => { lastActivity.current = Date.now(); }} onKeyDown={() => { lastActivity.current = Date.now(); }}>
      <div className="dialog card voice-sheet voice-talk">
        <audio ref={audio} autoPlay />
        <button type="button" className="btn ghost voice-close" onClick={finish} aria-label="Gespräch beenden">✕</button>
        <div className={`voice-orb talk-${phase}`} aria-hidden="true">🎙️</div>
        <p className="voice-timer" role="status">{labels[phase]}</p>
        {error && <p className="error">{error}</p>}
        <ul className="voice-lines" aria-live="polite">
          {lines.map((l, i) => <li key={i} className={`line-${l.who}`}>{l.text}</li>)}
        </ul>
        {plan && <PlanDecision key={plan.id} planId={plan.id} onDone={() => { lastActivity.current = Date.now(); }} />}
        <p className="hint small">Speichern mit „trag ein“ oder „notier“. Beenden mit „beenden“, „Tschüss“ oder ✕.</p>
      </div>
    </div>
  );
}
